import { Mail, Store, Clock, GitBranch, Phone, Tag, Flag, ClipboardList, UserCog, Rocket } from "lucide-react";

export const STAP_TYPES = {
  email: { label: "E-mail", icon: Mail, kleur: "bg-sky-50 text-sky-700 border-sky-200",
    config: { teksten: { nl: { onderwerp: "", tekst: "" } } } },
  webshop_uitnodiging: { label: "Webshop-uitnodiging", icon: Store, kleur: "bg-teal-50 text-teal-700 border-teal-200",
    config: { aanbodtekst: "", vervalt_dagen: 30 } },
  wacht: { label: "Wacht", icon: Clock, kleur: "bg-amber-50 text-amber-700 border-amber-200",
    config: { soort: "werkdagen", duur: 3 } },
  voorwaarde: { label: "Voorwaarde", icon: GitBranch, kleur: "bg-violet-50 text-violet-700 border-violet-200",
    config: { condities: { veld: "webshop_status", operator: "is", waarde: ["geactiveerd", "besteld"] } }, takken: ["ja", "nee"] },
  beltaak: { label: "Beltaak", icon: Phone, kleur: "bg-rose-50 text-rose-700 border-rose-200",
    config: { reden: "", script: "", prioriteit: "omzet_12m", deadline_werkdagen: 3, in_herinneringen: true }, takken: ["ja", "nee"] },
  kenmerk: { label: "Kenmerk", icon: Tag, kleur: "bg-lime-50 text-lime-700 border-lime-200",
    config: { actie: "toevoegen", categorie: "Campagne", waarde: "" } },
  verkoopkans: { label: "Verkoopkans", icon: Rocket, kleur: "bg-emerald-50 text-emerald-700 border-emerald-200",
    config: { naam: "", fase: "Cold", product_lijst: [], template_bedrag: 0, in_herinneringen: true } },
  status: { label: "Status", icon: UserCog, kleur: "bg-stone-100 text-stone-700 border-stone-200", config: { status: "klant" } },
  taak: { label: "Taak", icon: ClipboardList, kleur: "bg-orange-50 text-orange-700 border-orange-200",
    config: { omschrijving: "", deadline_werkdagen: 3 } },
  stop: { label: "Stop / doel", icon: Flag, kleur: "bg-neutral-900 text-white border-neutral-900", config: { reden: "" } },
};

export const CONDITIE_VELDEN = [
  { veld: "webshop_status", label: "Webshop-status", soort: "meerkeuze", opties: ["geen", "uitgenodigd", "geactiveerd", "besteld"] },
  { veld: "webshop_order_sinds_inschrijving", label: "Webshoporder sinds inschrijving", soort: "bool" },
  { veld: "mail_geopend", label: "Mail geopend (eerdere stap)", soort: "stap" },
  { veld: "mail_geklikt", label: "Mail geklikt (eerdere stap)", soort: "stap" },
  { veld: "laatste_order_dagen", label: "Laatste order (dagen geleden)", soort: "getal" },
  { veld: "omzet_12m", label: "Omzet 12 maanden (€)", soort: "getal" },
  { veld: "aantal_orders_12m", label: "Aantal orders 12 maanden", soort: "getal" },
  { veld: "heeft_kenmerk", label: "Heeft kenmerk", soort: "tekst" },
  { veld: "land", label: "Land", soort: "tekstlijst" },
  { veld: "status", label: "Organisatiestatus", soort: "meerkeuze", opties: ["suspect", "prospect", "klant", "inactief"] },
  { veld: "bel_uitkomst", label: "Bel-uitkomst (eerdere beltaak)", soort: "stap-uitkomst" },
  { veld: "heeft_telefoon", label: "Heeft telefoonnummer", soort: "bool" },
  { veld: "heeft_email", label: "Heeft e-mailadres", soort: "bool" },
];

export const VARIABELEN = [
  "{{organisatie.naam}}", "{{organisatie.klantcode}}", "{{organisatie.plaats}}", "{{contact.voornaam}}", "{{contact.aanhef}}",
  "{{webshop.activatie_link}}", "{{webshop.login_link}}", "{{laatste_order.datum}}", "{{laatste_order.bedrag}}",
  "{{laatste_order.regels}}", "{{omzet_12m}}", "{{campagne.naam}}", "{{afzender.naam}}", "{{afmeld_link}}",
];

export function nieuweStap(type) {
  const def = STAP_TYPES[type];
  const takken = {};
  (def.takken || []).forEach(t => { takken[t] = []; });
  return { id: crypto.randomUUID(), type, naam: def.label, config: JSON.parse(JSON.stringify(def.config)), takken };
}

export function alleStappen(stappen) {
  const out = [];
  (stappen || []).forEach(s => { out.push(s); Object.values(s.takken || {}).forEach(t => out.push(...alleStappen(t))); });
  return out;
}

const OPERATOR_TXT = { groter: ">", kleiner: "<", is: "=", niet: "≠" };

export function conditieTekst(c) {
  if (!c) return "—";
  if (c.op && c.items) return c.items.map(conditieTekst).join(c.op === "en" ? " EN " : " OF ");
  const v = CONDITIE_VELDEN.find(x => x.veld === c.veld);
  const w = Array.isArray(c.waarde) ? c.waarde.join("/") : (c.waarde ?? "");
  if (v?.soort === "bool" || v?.soort === "stap") return `${v.label}${c.operator === "niet" ? ": nee" : ": ja"}`;
  if (c.veld === "heeft_kenmerk") return `${c.operator === "niet" ? "Heeft niet" : "Heeft"} kenmerk ${c.categorie ? `${c.categorie}: ` : ""}${w}`;
  return `${v?.label || c.veld} ${OPERATOR_TXT[c.operator] || c.operator || "="} ${w}`;
}

export function stapSamenvatting(s) {
  const c = s.config || {};
  switch (s.type) {
    case "email": { if (c.design_id) return "HTML-ontwerp (huisstijl)"; const t = c.teksten?.nl || {}; return t.onderwerp ? `“${t.onderwerp}” · ${Object.keys(c.teksten || {}).join("/").toUpperCase()}` : "nog geen onderwerp"; }
    case "webshop_uitnodiging": return c.aanbodtekst ? `aanbod: ${c.aanbodtekst.slice(0, 60)}` : "standaard uitnodiging (taal per land)";
    case "wacht": return c.soort === "datum" ? `tot ${c.datum || "?"}` : c.soort === "weekdag" ? `tot weekdag ${c.weekdag} ${c.tijd || ""}` : `${c.duur} ${c.soort}`;
    case "voorwaarde": return conditieTekst(c.condities);
    case "beltaak": return `${c.reden || "geen reden"} · deadline ${c.deadline_werkdagen} werkdagen · prio ${c.prioriteit}`;
    case "kenmerk": return `${c.actie} “${c.categorie ? `${c.categorie}: ` : ""}${c.waarde || "?"}”`;
    case "verkoopkans": return `${c.naam || "naam van campagne"} · fase ${c.fase || "Cold"} · ${(c.product_lijst || []).length ? `${c.product_lijst.length} artikel(en)` : `bedrag € ${c.template_bedrag || 0}`}`;
    case "status": return `status → ${c.status}`;
    case "taak": return c.omschrijving || "taak voor eigenaar";
    case "stop": return c.reden || "einde van de flow";
    default: return "";
  }
}
